"use client";

import { memo, useEffect, useRef, useState } from "react";

// Shared building blocks for the agent office and the homepage story scenes.

export const INK = "#25272D";
/** Text/line colour that adapts to the theme (INK stays fixed for character outlines and dark chips). */
export const TXT = "var(--ink)";
export const ACCENT = "#2E51D6";
export const MONO = "var(--font-geist-mono), ui-monospace, monospace";

export const ENGINES = [
  { name: "ChatGPT", color: "#10A37F" },
  { name: "Gemini", color: "#4285F4" },
  { name: "Perplexity", color: "#20808D" },
  { name: "Claude", color: "#D97757" },
  { name: "Copilot", color: "#0078D4" },
  { name: "AI Overviews", color: "#F2A900" },
];

export const QUESTIONS = [
  "best CNC machining supplier in India?",
  "top ERP implementation partners?",
  "industrial valve exporters for EU?",
  "reliable PCB makers for export?",
  "B2B SaaS for dealer management?",
  "IT services firm for cloud migration?",
];

export type Pt = [number, number];
export type Hair = "side" | "spiky" | "bun" | "beanie" | "long" | "cap" | "curly";
export type Look = {
  shirt: string; pants: string; skin: string; hair: string; style: Hair;
  acc?: "glasses" | "headset" | "pencil" | "beret" | "badge" | "clipboard" | "palette" | "hardhat" | "phone" | "flag";
  accColor?: string; // headset colour (defaults to INK)
};

export const dist = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
export const pathLen = (path: Pt[]) => path.slice(1).reduce((s, p, i) => s + dist(path[i], p), 0);

export function along(path: Pt[], k: number): Pt {
  let d = Math.max(0, Math.min(1, k)) * pathLen(path);
  for (let i = 1; i < path.length; i++) {
    const seg = dist(path[i - 1], path[i]);
    if (d <= seg || i === path.length - 1) {
      const f = seg ? Math.min(1, d / seg) : 0;
      return [path[i - 1][0] + (path[i][0] - path[i - 1][0]) * f, path[i - 1][1] + (path[i][1] - path[i - 1][1]) * f];
    }
    d -= seg;
  }
  return path[path.length - 1];
}

/* ---------- characters ---------- */

function HairBack({ x, y, l }: { x: number; y: number; l: Look }) {
  if (l.style === "long") return <rect x={x - 13} y={y - 4} width={26} height={20} rx={8} fill={l.hair} />;
  return null;
}

function HairTop({ x, y, l }: { x: number; y: number; l: Look }) {
  switch (l.style) {
    case "side":
      return <ellipse cx={x - 3} cy={y - 7} rx={10} ry={4.5} transform={`rotate(-14 ${x - 3} ${y - 7})`} fill={l.hair} />;
    case "spiky":
      return <polygon points={`${x - 12},${y - 4} ${x - 9},${y - 15} ${x - 4},${y - 8} ${x},${y - 17} ${x + 4},${y - 8} ${x + 9},${y - 15} ${x + 12},${y - 4}`} fill={l.hair} />;
    case "bun":
      return <circle cx={x} cy={y - 15} r={6} fill={l.hair} />;
    case "beanie":
      return (
        <g>
          <path d={`M${x - 13} ${y - 2} A13 13 0 0 1 ${x + 13} ${y - 2} Z`} fill="#F97316" />
          <rect x={x - 13.5} y={y - 4} width={27} height={5} rx={2.5} fill="#EA580C" />
          <circle cx={x} cy={y - 15} r={3.5} fill="#FED7AA" />
        </g>
      );
    case "cap":
      return (
        <g>
          <path d={`M${x - 12.5} ${y - 3} A12.5 12.5 0 0 1 ${x + 12.5} ${y - 3} Z`} fill={ACCENT} />
          <rect x={x + 6} y={y - 5} width={12} height={4} rx={2} fill="#1E3A8A" />
        </g>
      );
    case "curly":
      return (
        <g fill={l.hair}>
          {[-10, -5, 0, 5, 10].map((dx, i) => (
            <circle key={dx} cx={x + dx} cy={y - 9 - (i % 2) * 3} r={5} />
          ))}
        </g>
      );
    case "long":
      return <ellipse cx={x + 2} cy={y - 7} rx={11} ry={4.5} transform={`rotate(10 ${x + 2} ${y - 7})`} fill={l.hair} />;
  }
}

export function Rep({ at, l, t, seed, walking = false, stride = 0, carrying = false, typing = false }: {
  at: Pt; l: Look; t: number; seed: number; walking?: boolean; stride?: number; carrying?: boolean; typing?: boolean;
}) {
  const [x, y] = at;
  const hy = y - 37; // head centre
  const s = Math.sin(stride);
  const legL = walking && s > 0 ? -s * 3 : 0;
  const legR = walking && s < 0 ? s * 3 : 0;
  const bounce = walking ? -Math.abs(s) * 1.2 : 0;
  const blink = (t + seed * 1.37) % 4.2 < 0.13;
  const tapL = typing ? Math.max(0, Math.sin(t * 14 + seed)) * 2 : 0;
  const tapR = typing ? Math.max(0, Math.sin(t * 14 + seed + 2)) * 2 : 0;
  return (
    <g transform={`translate(0 ${bounce})`}>
      <ellipse cx={x} cy={y} rx={10} ry={3} fill="rgba(37,39,45,0.13)" />
      <rect x={x - 6} y={y - 10 + legL} width={5} height={10} rx={2} fill={l.pants} />
      <rect x={x + 1} y={y - 10 + legR} width={5} height={10} rx={2} fill={l.pants} />
      <HairBack x={x} y={hy} l={l} />
      <rect x={x - 9} y={y - 25} width={18} height={17} rx={7} fill={l.shirt} />
      {l.acc === "badge" && <circle cx={x + 4} cy={y - 18} r={2.6} fill="#FFFFFF" stroke="#16A34A" strokeWidth={1.2} />}
      {carrying ? (
        <g>
          <rect x={x - 7} y={y - 25} width={14} height={11} rx={1.5} fill="var(--il-panel)" stroke="var(--il-line)" strokeWidth={0.8} />
          <line x1={x - 4.5} y1={y - 21.5} x2={x + 4.5} y2={y - 21.5} stroke="var(--il-mute)" strokeWidth={1} />
          <line x1={x - 4.5} y1={y - 18.5} x2={x + 2.5} y2={y - 18.5} stroke="var(--il-mute)" strokeWidth={1} />
          <circle cx={x - 7.5} cy={y - 17} r={2.6} fill={l.skin} />
          <circle cx={x + 7.5} cy={y - 17} r={2.6} fill={l.skin} />
        </g>
      ) : (
        <g>
          <circle cx={x - 10} cy={y - 15 - tapL} r={2.6} fill={l.skin} />
          <circle cx={x + 10} cy={y - 15 - tapR} r={2.6} fill={l.skin} />
        </g>
      )}
      <circle cx={x} cy={hy - 2} r={13} fill={l.style === "beanie" || l.style === "cap" ? l.skin : l.hair} />
      <ellipse cx={x} cy={hy + 2} rx={10.5} ry={10} fill={l.skin} />
      <HairTop x={x} y={hy} l={l} />
      {blink ? (
        <g stroke={INK} strokeWidth={1.4} strokeLinecap="round">
          <line x1={x - 6} y1={hy + 3} x2={x - 3} y2={hy + 3} />
          <line x1={x + 3} y1={hy + 3} x2={x + 6} y2={hy + 3} />
        </g>
      ) : (
        <g fill={INK}>
          <ellipse cx={x - 4.5} cy={hy + 3} rx={1.7} ry={2.1} />
          <ellipse cx={x + 4.5} cy={hy + 3} rx={1.7} ry={2.1} />
        </g>
      )}
      <circle cx={x - 7.2} cy={hy + 7} r={2} fill="#FF8FA3" opacity={0.55} />
      <circle cx={x + 7.2} cy={hy + 7} r={2} fill="#FF8FA3" opacity={0.55} />
      <path d={`M${x - 2.2} ${hy + 7.5} Q${x} ${hy + 9.5} ${x + 2.2} ${hy + 7.5}`} stroke={INK} strokeWidth={1.1} fill="none" strokeLinecap="round" />
      {l.acc === "glasses" && (
        <g stroke={INK} strokeWidth={1} fill="rgba(255,255,255,0.25)">
          <circle cx={x - 4.5} cy={hy + 3} r={3.6} />
          <circle cx={x + 4.5} cy={hy + 3} r={3.6} />
          <line x1={x - 0.9} y1={hy + 3} x2={x + 0.9} y2={hy + 3} />
        </g>
      )}
      {l.acc === "headset" && (
        <g>
          <path d={`M${x - 12} ${hy + 2} A12 12 0 0 1 ${x + 12} ${hy + 2}`} stroke={l.accColor ?? INK} strokeWidth={2} fill="none" />
          <rect x={x - 14} y={hy - 1} width={4} height={7} rx={2} fill={l.accColor ?? INK} />
          <rect x={x + 10} y={hy - 1} width={4} height={7} rx={2} fill={l.accColor ?? INK} />
          <path d={`M${x - 12} ${hy + 5} Q${x - 9} ${hy + 11} ${x - 4} ${hy + 10}`} stroke={l.accColor ?? INK} strokeWidth={1.2} fill="none" />
        </g>
      )}
      {l.acc === "pencil" && <rect x={x + 3} y={hy - 21} width={2.4} height={10} rx={1} fill="#F2A900" transform={`rotate(25 ${x + 4} ${hy - 16})`} />}
      {l.acc === "beret" && <ellipse cx={x - 2} cy={hy - 11} rx={11} ry={4.5} fill="#DC2626" transform={`rotate(-10 ${x - 2} ${hy - 11})`} />}
      {l.acc === "clipboard" && (
        <g>
          <rect x={x - 22} y={y - 31} width={11} height={14} rx={1.5} fill="#A16207" />
          <rect x={x - 20.5} y={y - 28.5} width={8} height={10} rx={1} fill="var(--il-panel)" />
          <rect x={x - 19.5} y={y - 32.5} width={6} height={3} rx={1} fill="#64748B" />
          <line x1={x - 19} y1={y - 25} x2={x - 14} y2={y - 25} stroke="var(--il-mute)" strokeWidth={1} />
          <line x1={x - 19} y1={y - 22} x2={x - 15} y2={y - 22} stroke="var(--il-mute)" strokeWidth={1} />
        </g>
      )}
      {l.acc === "palette" && (
        <g>
          <ellipse cx={x + 17} cy={y - 25} rx={7.5} ry={5.5} fill="#F5DEB3" stroke="var(--il-desk-3)" strokeWidth={0.8} />
          <circle cx={x + 14} cy={y - 27} r={1.6} fill="#DC2626" />
          <circle cx={x + 18} cy={y - 28} r={1.6} fill={ACCENT} />
          <circle cx={x + 21.5} cy={y - 25.5} r={1.6} fill="#F2A900" />
          <circle cx={x + 17} cy={y - 23} r={1.6} fill="#22A06B" />
        </g>
      )}
      {l.acc === "hardhat" && (
        <g>
          <path d={`M${x - 13} ${hy - 3} A13 13 0 0 1 ${x + 13} ${hy - 3} Z`} fill="#FACC15" />
          <rect x={x - 15.5} y={hy - 5} width={31} height={4} rx={2} fill="#EAB308" />
          <rect x={x - 1.5} y={hy - 15} width={3} height={10} rx={1.5} fill="#EAB308" />
        </g>
      )}
      {l.acc === "flag" && (
        <g>
          <line x1={x + 13} y1={y - 15} x2={x + 13} y2={y - 58} stroke="#64748B" strokeWidth={1.6} strokeLinecap="round" />
          <path d={`M${x + 13} ${y - 58} L${x + 27} ${y - 53} L${x + 13} ${y - 48} Z`} fill={l.accColor ?? ACCENT} />
        </g>
      )}
      {l.acc === "phone" && (
        <g>
          <rect x={x + 10} y={hy - 4} width={5} height={12} rx={1.5} fill={INK} />
          <circle cx={x + 13} cy={hy + 9} r={2.6} fill={l.skin} />
        </g>
      )}
    </g>
  );
}

export function Bot({ x, y, t, busy }: { x: number; y: number; t: number; busy: boolean }) {
  const swing = busy ? Math.sin(t * 8) * 5 : 0;
  const blink = t % 3.6 < 0.15;
  return (
    <g>
      <ellipse cx={x} cy={y} rx={14} ry={3.5} fill="rgba(37,39,45,0.13)" />
      <line x1={x - 14} y1={y - 20} x2={x - 22} y2={y - 12 + swing} stroke="#94A3B8" strokeWidth={3} strokeLinecap="round" />
      <line x1={x + 14} y1={y - 20} x2={x + 22} y2={y - 12 - swing} stroke="#94A3B8" strokeWidth={3} strokeLinecap="round" />
      <rect x={x - 14} y={y - 30} width={28} height={24} rx={7} fill="#CBD5E1" />
      <rect x={x - 6} y={y - 22} width={12} height={6} rx={2} fill={busy ? "#22C55E" : "#94A3B8"} />
      <line x1={x} y1={y - 52} x2={x} y2={y - 60} stroke="#94A3B8" strokeWidth={2} />
      <circle cx={x} cy={y - 62} r={3} fill={busy && Math.sin(t * 10) > 0 ? "#EF4444" : "#F59E0B"} />
      <rect x={x - 13} y={y - 53} width={26} height={21} rx={6} fill="#3B3F4A" />
      <rect x={x - 8} y={y - 46} width={5} height={blink ? 1.5 : 6} rx={1.5} fill="#67E8F9" />
      <rect x={x + 3} y={y - 46} width={5} height={blink ? 1.5 : 6} rx={1.5} fill="#67E8F9" />
    </g>
  );
}

/* ---------- furniture ---------- */

export const Desk = memo(function Desk({ x, sy, glow, metal = false }: { x: number; sy: number; glow: boolean; metal?: boolean }) {
  return (
    <g>
      <rect x={x - 75} y={sy - 10} width={150} height={17} rx={4} fill={metal ? "var(--il-metal)" : "var(--il-desk)"} />
      <rect x={x - 72} y={sy + 7} width={144} height={30} rx={3} fill={metal ? "var(--il-metal-2)" : "var(--il-desk-2)"} />
      <line x1={x - 20} y1={sy + 14} x2={x + 20} y2={sy + 14} stroke={metal ? "var(--il-metal-3)" : "var(--il-desk-3)"} strokeWidth={2} strokeLinecap="round" />
      {!metal && (
        <>
          <rect x={x + 14} y={sy - 25} width={32} height={18} rx={2.5} fill="var(--il-line)" />
          <rect x={x + 14} y={sy - 25} width={32} height={2.5} rx={1.2} fill={glow ? "#7EA2FF" : "var(--il-line)"} />
          <circle cx={x + 30} cy={sy - 15} r={2.2} fill={ACCENT} opacity={0.6} />
          <rect x={x + 10} y={sy - 8} width={40} height={4} rx={2} fill="var(--il-line)" />
          <rect x={x - 60} y={sy - 15} width={9} height={10} rx={2} fill="var(--il-panel)" stroke="var(--il-desk-2)" />
          <rect x={x - 42} y={sy - 9} width={18} height={4} rx={1} fill="var(--il-panel)" />
          <rect x={x - 41} y={sy - 12} width={18} height={4} rx={1} fill="#F8FAFC" />
        </>
      )}
    </g>
  );
});

export function Plant({ x, y, s = 1 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx={0} cy={0} rx={10} ry={3} fill="rgba(37,39,45,0.12)" />
      <rect x={-7} y={-12} width={14} height={12} rx={3} fill="#C08457" />
      <ellipse cx={-5} cy={-19} rx={5} ry={9} fill="#4ADE80" transform="rotate(-25 -5 -19)" />
      <ellipse cx={5} cy={-19} rx={5} ry={9} fill="#22C55E" transform="rotate(25 5 -19)" />
      <ellipse cx={0} cy={-23} rx={4.5} ry={9} fill="#86EFAC" />
    </g>
  );
}

/* ---------- labels ---------- */

export function Bubble({ x, y, text, tone = "light", vbW = 1200, bounds }: { x: number; y: number; text: string; tone?: "light" | "accent"; vbW?: number; bounds?: [number, number] }) {
  const w = text.length * 6.35 + 18;
  const [lo, hi] = bounds ?? [28, vbW - 28];
  const bx = Math.min(Math.max(x, lo + w / 2), hi - w / 2); // keep the box inside the walls; the tail still points at the speaker
  const top = y - 24;
  const fill = tone === "accent" ? ACCENT : "var(--il-panel)";
  return (
    <g>
      <rect x={bx - w / 2} y={top} width={w} height={18} rx={4} fill={fill} stroke={tone === "accent" ? ACCENT : "var(--il-line)"} strokeWidth={1} />
      <path d={`M${x - 4} ${top + 17.5} L${x} ${top + 23} L${x + 4} ${top + 17.5}`} fill={fill} stroke={tone === "accent" ? ACCENT : "var(--il-line)"} strokeWidth={1} />
      <rect x={x - 4.5} y={top + 16} width={9} height={2.2} fill={fill} />
      <text x={bx} y={top + 12.5} textAnchor="middle" fontFamily={MONO} fontSize={10.5} fontWeight={600} fill={tone === "accent" ? "#FFFFFF" : TXT}>
        {text}
      </text>
    </g>
  );
}

export function Tag({ x, y, text }: { x: number; y: number; text: string }) {
  const w = text.length * 6 + 14;
  return (
    <g>
      <rect x={x - w / 2} y={y} width={w} height={16} rx={4} fill={INK} />
      <text x={x} y={y + 11.3} textAnchor="middle" fontFamily={MONO} fontSize={9.5} fontWeight={700} fill="#FFFFFF">
        {text}
      </text>
    </g>
  );
}

// The page guide has its own look (navy shirt, tour-guide flag) so it never looks like a scene agent.
export const GUIDE_LOOK: Look = { shirt: "#1E3A8A", pants: "#334155", skin: "#F6C9A0", hair: "#6B3F1F", style: "side", acc: "flag" };

// Scroll-driven animation time shared by the office and the story scenes.
// `progress()` maps the scroll position to 0..1, which maps to start..end.
// The shown time eases toward that target, so wheel steps still look smooth.
// Frozen at `still` for people who prefer reduced motion.
export function useScrollTime(progress: () => number, start: number, end: number, still: number) {
  const [t, setT] = useState(start);
  const progressRef = useRef(progress);
  useEffect(() => {
    progressRef.current = progress;
  });
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setT(still);
      return;
    }
    let cur = start;
    let raf = 0;
    const step = () => {
      const target = start + (end - start) * Math.max(0, Math.min(1, progressRef.current()));
      const d = target - cur;
      cur = Math.abs(d) < 0.005 ? target : cur + d * 0.18;
      setT(cur);
      raf = cur === target ? 0 : requestAnimationFrame(step);
    };
    const kick = () => {
      if (!raf) raf = requestAnimationFrame(step);
    };
    kick();
    window.addEventListener("scroll", kick, { passive: true });
    window.addEventListener("resize", kick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", kick);
      window.removeEventListener("resize", kick);
    };
  }, [start, end, still]);
  return t;
}

// What the guide carries changes with the story: question → data → page → enquiry.
export function GuideCarry({ x, y, stage }: { x: number; y: number; stage: number }) {
  const cx = x;
  const cy = y - 19.5;
  if (stage < 3)
    return (
      <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize={9} fontWeight={800} fill={ACCENT}>
        ?
      </text>
    );
  if (stage < 5)
    return (
      <g fill={ACCENT}>
        <rect x={cx - 4} y={cy} width={2} height={3} />
        <rect x={cx - 1} y={cy - 2} width={2} height={5} />
        <rect x={cx + 2} y={cy - 4} width={2} height={7} />
      </g>
    );
  if (stage < 7) return <circle cx={cx + 4.5} cy={cy - 3.5} r={2.6} fill="#22C55E" />;
  return (
    <g>
      <rect x={cx - 7} y={cy - 5.5} width={14} height={10} rx={1.5} fill="var(--il-panel)" stroke={ACCENT} />
      <path d={`M${cx - 7} ${cy - 5} L${cx} ${cy + 0.5} L${cx + 7} ${cy - 5}`} fill="none" stroke={ACCENT} />
    </g>
  );
}
